import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useError } from '@zenbusiness/application-commons-logging';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useTaxClassification } from '#client/pages/ProductLanding/hooks/useTaxClassification';
/**
 * Component that checks if the business entity has a tax classification
 * and redirects to the redirect path if the condition is met.
 * @param {object} props
 * @param {string} [props.flagKey] - The flag key to check.
 * @param {Function} [props.shouldRedirect] - A conditional function that returns a truthy value to trigger the redirection.
 * @param {string|Function} [props.redirectPath] - The path to redirect to (processed by useDashboardPath). Accepts a string or a function receiving { taxClassification, flagValue, entitlements } that returns a string.
 * @param {import('react').ReactNode} [props.children] - The children to render.
 * @component
 */
function TaxClassificationGate({ flagKey, shouldRedirect = () => false, redirectPath = '/products', children }) {
  const { taxClassification, loading, error } = useTaxClassification();
  const flagValue = useExperimentFlag(flagKey || '', false, false, !flagKey);
  const { entitlements } = useBusinessEntity();

  const resolvedPath =
    typeof redirectPath === 'function' ? redirectPath({ taxClassification, flagValue, entitlements }) : redirectPath;

  const dashboardRedirectPath = useDashboardPath(resolvedPath);

  useError(error);
  if (error) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  if (loading || !taxClassification) {
    return <PageLoading />;
  }

  if (shouldRedirect({ taxClassification, flagValue, entitlements })) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  return children;
}

TaxClassificationGate.propTypes = {
  flagKey: PropTypes.string,
  shouldRedirect: PropTypes.func,
  redirectPath: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  children: PropTypes.node.isRequired,
};

export default TaxClassificationGate;
